import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Text } from "rebass";
import { ROOT_FOLDER } from "../../../store/modules/library/constants";
import {
  getActiveFolder,
  getActiveFolderItems,
  getActiveRecord,
  getActiveRecordContent,
} from "../../../store/modules/library/selectors";

import { FolderLink } from "./FolderLink";
import { RecordLink } from "./RecordLink";
import { SpinnerSVG } from "../svg/SpinnerSVG";
import { LI } from "../styled/LI";
import { UL } from "../styled/UL";

import { ContentMenu } from "./ContentMenu";
import { InstanceType } from "../../../utils/Instance";
import { ContentMenuPosition } from "./ContentMenu/types";

import { ActiveType, useSetActive } from "../../hooks/useSetActive";

interface IProps {
  isScrolling: boolean;
}

interface Item {
  dateAdded: number;
}
const sortByDateAdded = (a: Item, b: Item) => b.dateAdded - a.dateAdded;

export const LibraryContent: React.FC<IProps> = ({ isScrolling }) => {
  const ulRef = React.createRef<HTMLUListElement>();
  const [activeInstance, setActiveInstance] = useState<string | null>(null);
  const [contentMenuPosition, setContentMenuPosition] = useState<ContentMenuPosition | null>(null);

  const activeFolder = useSelector(getActiveFolder);
  const activeFolderItems = useSelector(getActiveFolderItems);
  const activeRecord = useSelector(getActiveRecord);
  const activeRecordContent = useSelector(getActiveRecordContent);

  const openContentMenu = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    event.preventDefault();
    const { currentTarget } = event;
    const { offsetParent } = currentTarget;

    if (offsetParent) {
      const {
        top: offsetParentTop,
        bottom: offsetParentBottom,
      } = offsetParent.getBoundingClientRect();
      const {
        top: currentTargetTop,
        bottom: currentTargetBottom,
      } = currentTarget.getBoundingClientRect();
      const isVisible = (
        (currentTargetTop > offsetParentTop)
        && (currentTargetBottom < offsetParentBottom)
      );

      if (isVisible) {
        setActiveInstance(currentTarget.getAttribute("value"));

        setContentMenuPosition({
          currentTarget: {
            top: currentTargetTop,
            bottom: currentTargetBottom,
          },
          offsetParent: {
            top: offsetParentTop,
            bottom: offsetParentBottom,
          },
        });
      }
    }
  }, []);
  const closeContentMenu = useCallback(() => {
    setActiveInstance(null);
    setContentMenuPosition(null);
  }, []);

  useSetActive(ActiveType.folder);

  useEffect(() => {
    if (isScrolling) {
      closeContentMenu();
    }
  }, [isScrolling, closeContentMenu]);

  useEffect(() => {
    const { current } = ulRef;

    if (activeInstance && current) {
      const listener = (event: MouseEvent) => {
        if (!current.contains(event.target as HTMLElement)) {
          closeContentMenu();
        }
      };

      window.addEventListener("mousedown", listener);

      return () => {
        window.removeEventListener("mousedown", listener);
      };
    }
  }, [activeInstance, ulRef, closeContentMenu]);

  if (!activeFolderItems) {
    return (!activeFolder && !activeRecord)
      ? (
        <Box
          color="sp-blue"
          css="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"
        >
          <SpinnerSVG size={26} />
        </Box>
      )
      : (
        <UL
          ref={ulRef}
          onClick={closeContentMenu}
        >
          <ContentMenu
            position={contentMenuPosition}
            activeInstance={activeInstance}
          />

          {
            !activeFolder && (
              <LI>
                <FolderLink folder={{ id: ROOT_FOLDER, title: "Library" }} />
              </LI>
            )
          }

          {
            activeRecordContent && (
              <LI
                value={[activeRecordContent.id, InstanceType.RECORD]}
                isActive={`${activeRecordContent.id},${InstanceType.RECORD}` === activeInstance}
                onContextMenu={openContentMenu}
              >
                <RecordLink record={activeRecordContent} />
              </LI>
            )
          }
        </UL>
      );
  }

  const folders = Object.values(activeFolderItems.folders);
  const records = Object.values(activeFolderItems.records).sort(sortByDateAdded);

  if (folders.length === 0 && records.length === 0) {
    return (
      <Text
        px="30px"
        py="15px"
        color="zodiac"
      >
        No items is in the folder yet
      </Text>
    );
  }

  return (
    <UL
      ref={ulRef}
      onClick={closeContentMenu}
    >
      <ContentMenu
        position={contentMenuPosition}
        activeInstance={activeInstance}
      />

      {
        folders.map((folder) => (
          <LI
            key={folder.id}
            value={[folder.id, InstanceType.FOLDER]}
            isActive={`${folder.id},${InstanceType.FOLDER}` === activeInstance}
            onContextMenu={openContentMenu}
          >
            <FolderLink folder={folder} />
          </LI>
        ))
      }

      {
        records.map((record) => (
          <LI
            key={record.id}
            value={[record.id, InstanceType.RECORD]}
            isActive={`${record.id},${InstanceType.FOLDER}` === activeInstance}
            onContextMenu={openContentMenu}
          >
            <RecordLink record={record} />
          </LI>
        ))
      }
    </UL>
  );
};
